import { useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { PersonalizedPromptPoolResponse, PersonalizedPromptSurface } from '@bower/shared';
import { useWorkspaceId } from '../../contexts/WorkspaceContext';
import { personalizationService } from '../../services/personalization.service';

const MAX_ONBOARDING_ARTIFACT_IDS = 20;

function normalizeArtifactIds(artifactIds: readonly string[]): string[] {
  return [...new Set(artifactIds.filter(Boolean))].sort().slice(0, MAX_ONBOARDING_ARTIFACT_IDS);
}

export const personalizedPromptPoolKey = (
  workspaceId: string | null,
  surface: PersonalizedPromptSurface,
  artifactIds: readonly string[] = [],
  freshnessKey?: string,
  generation = 0,
) =>
  [
    'personalizedPromptPool',
    workspaceId,
    surface,
    normalizeArtifactIds(artifactIds),
    generation,
    ...(freshnessKey === undefined ? [] : [freshnessKey]),
  ] as const;

export function usePersonalizedPrompts(
  surface: PersonalizedPromptSurface,
  artifactIds: readonly string[] = [],
  freshnessKey?: string,
  enabled = true,
  generation = 0,
) {
  const workspaceId = useWorkspaceId();
  const normalizedIds = useMemo(() => normalizeArtifactIds(artifactIds), [artifactIds]);

  return useQuery<PersonalizedPromptPoolResponse>({
    queryKey: personalizedPromptPoolKey(
      workspaceId,
      surface,
      normalizedIds,
      freshnessKey,
      generation,
    ),
    // Never coerce a transport or envelope failure to `[]`: the caller needs
    // a retry affordance. The server may deliberately return an empty,
    // `isDegraded` pool when optional signal gathering fails; that is a
    // successful, non-page-breaking fallback and stays distinguishable.
    queryFn: async () => {
      const response = await personalizationService.getPromptPool(
        surface,
        normalizedIds,
        generation,
      );
      if (!response.success || !('data' in response) || !response.data) {
        throw new Error('Failed to load personalized prompts');
      }
      return response.data;
    },
    enabled: enabled && !!workspaceId,
    staleTime: surface === 'onboarding' ? 5_000 : 60_000,
    // Prompt rotation changes only the suggested cards. Keep the preceding
    // set visible while the next deterministic server selection loads.
    placeholderData: keepPreviousData,
  });
}
